import { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Alert, Button, Table, Tag, Typography } from 'antd';
import { IconArrowLeft, IconAlertOctagon, IconPlus, IconAlertTriangle } from '@tabler/icons-react';
import type { ColumnsType } from 'antd/es/table';

import { useEmployeeTrainingProfile, useEmployeeAssignments } from '@/entities/training/api/libraryApi';
import {
  assignmentStatusLabels,
  programKindLabels,
  type AssignmentStatus,
  type EmployeeAssignment,
  type ProgramKind,
} from '@/entities/training/model/librarySchema';
import { trainingAssignmentStatusTagColor } from '@/entities/training/model/trainingTableTagColors';

import { PageSpinner } from '@/shared/ui/PageSpinner';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { ProgramDetailDrawer } from '../LibraryTab/ProgramDetailDrawer';
import s from './EmployeeTrainingCard.module.scss';

const { Title, Text } = Typography;

function formatDueRu(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
}

const assignmentColumns: ColumnsType<EmployeeAssignment> = [
  {
    title: 'Программа',
    key: 'program',
    render: (_: unknown, a: EmployeeAssignment) => (
      <>
        {a.program.name}
        {a.level ? <Text type="secondary"> — {a.level.level_name}</Text> : null}
      </>
    ),
  },
  {
    title: 'Вид',
    key: 'kind',
    width: 180,
    render: (_: unknown, a: EmployeeAssignment) =>
      a.program.program_kind
        ? (programKindLabels[a.program.program_kind as ProgramKind] ?? a.program.program_kind)
        : '—',
  },
  {
    title: 'Статус',
    key: 'status',
    width: 140,
    render: (_: unknown, a: EmployeeAssignment) => (
      <Tag color={trainingAssignmentStatusTagColor[a.status] ?? 'default'}>
        {assignmentStatusLabels[a.status as AssignmentStatus] ?? a.status}
      </Tag>
    ),
  },
  {
    title: 'Запланировано',
    dataIndex: 'planned_at',
    key: 'planned',
    width: 130,
    render: (v: string | null) => v ?? '—',
  },
  {
    title: 'Пройдено',
    dataIndex: 'completed_at',
    key: 'completed',
    width: 130,
    render: (v: string | null) => v ?? '—',
  },
  {
    title: 'Действует до',
    dataIndex: 'valid_until',
    key: 'valid',
    width: 130,
    render: (v: string | null) => v ?? '—',
  },
];

type TrainingEmployeeDetailProps = {
  employeeId: string;
  showBack?: boolean;
  hideProfile?: boolean;
  embeddedInEmployeeCard?: boolean;
};

/** Карточка обучения сотрудника (используется на маршруте и во вкладке «Мои обучения»). */
export function TrainingEmployeeDetail({
  employeeId,
  showBack,
  hideProfile,
  embeddedInEmployeeCard,
}: TrainingEmployeeDetailProps) {
  const navigate = useNavigate();
  const [matrixDrawerProgramId, setMatrixDrawerProgramId] = useState<string | null>(null);

  const { data: profile, isLoading: profileLoading } = useEmployeeTrainingProfile(employeeId);
  const { data: assignments, isLoading: assignmentsLoading } = useEmployeeAssignments(employeeId);

  const isAdmitted = profile?.work_admission === 'admitted';
  const employeeCardUrl = `/employees?employeeId=${employeeId}&tab=training`;

  const stats = useMemo(() => {
    if (!assignments) return { overdue: 0, expiring: 0, active: 0, completed: 0, total: 0 };
    const today = new Date();
    const horizon30 = new Date(today);
    horizon30.setDate(horizon30.getDate() + 30);

    const overdue = assignments.filter((a) => a.status === 'overdue' || a.status === 'not_passed').length;
    const expiring = assignments.filter((a) => {
      if (a.status === 'overdue' || a.status === 'not_passed') return false;
      if (!a.valid_until) return false;
      const d = new Date(a.valid_until);
      return d > today && d <= horizon30;
    }).length;
    const active = assignments.filter((a) => a.status === 'assigned' || a.status === 'in_progress').length;
    const completed = assignments.filter((a) => a.status === 'passed').length;
    return { overdue, expiring, active, completed, total: assignments.length };
  }, [assignments]);

  const activeAssignments = useMemo(() => (assignments ?? []).filter((a) => a.status !== 'passed'), [assignments]);

  if (profileLoading) {
    return <PageSpinner />;
  }

  if (!profile) {
    return (
      <div className={s.root}>
        {showBack ? (
          <Button icon={<IconArrowLeft size={16} />} type="text" onClick={() => navigate('/training')}>
            Назад
          </Button>
        ) : null}
        <Title level={4}>Сотрудник не найден</Title>
      </div>
    );
  }

  return (
    <div className={s.root}>
      {showBack ? (
        <div className={s.root__breadcrumbs}>
          <Link to="/training?tab=records" className={s.root__crumbLink}>
            ← К списку сотрудников
          </Link>
          <span className={s.root__crumbSep}>/</span>
          <span>{profile.full_name}</span>
        </div>
      ) : null}

      {!hideProfile ? (
        embeddedInEmployeeCard ? (
          <div className={s.root__hero}>
            <div className={s.root__heroAvatar}>
              {profile.full_name
                .split(' ')
                .slice(0, 2)
                .map((n) => n[0])
                .join('')}
            </div>
            <div className={s.root__heroInfo}>
              <div className={s.root__heroName} data-hero-name>
                {profile.full_name}
              </div>
              <div className={s.root__heroMeta}>{profile.position ?? '—'}</div>
              <div className={s.root__heroMeta}>{profile.department_name}</div>
              <div className={s.root__heroBadges}>
                {profile.personnel_number ? (
                  <Tag className={s.root__heroTagDark}>Таб. {profile.personnel_number}</Tag>
                ) : null}
                <StatusBadge
                  status={isAdmitted ? 'ok' : 'err'}
                  label={isAdmitted ? 'Допущен к работе' : 'Не допущен к работе'}
                />
              </div>
            </div>
          </div>
        ) : (
          <Link to={employeeCardUrl} className={s.root__heroLink}>
            <div className={s.root__hero}>
              <div className={s.root__heroAvatar}>
                {profile.full_name
                  .split(' ')
                  .slice(0, 2)
                  .map((n) => n[0])
                  .join('')}
              </div>
              <div className={s.root__heroInfo}>
                <div className={s.root__heroName} data-hero-name>
                  {profile.full_name}
                </div>
                <div className={s.root__heroMeta}>{profile.position ?? '—'}</div>
                <div className={s.root__heroMeta}>{profile.department_name}</div>
                <div className={s.root__heroBadges}>
                  {profile.personnel_number ? (
                    <Tag className={s.root__heroTagDark}>Таб. {profile.personnel_number}</Tag>
                  ) : null}
                  <StatusBadge
                    status={isAdmitted ? 'ok' : 'err'}
                    label={isAdmitted ? 'Допущен к работе' : 'Не допущен к работе'}
                  />
                </div>
              </div>
            </div>
          </Link>
        )
      ) : null}

      {!isAdmitted ? (
        <div className={s.root__problemAlert}>
          <IconAlertOctagon size={20} className={s.root__problemIcon} />
          <div className={s.root__problemContent}>
            <div className={s.root__problemTitle}>Не допущен к работе</div>
            <div className={s.root__problemDesc}>
              {stats.overdue > 0
                ? `Есть просроченные или не пройденные обучения (${stats.overdue}).`
                : 'Сотрудник не допущен к самостоятельной работе.'}
            </div>
          </div>
        </div>
      ) : null}

      <div className={s.root__statsRow}>
        <div className={s.root__stat}>
          <span className={`${s.root__statValue} ${stats.overdue > 0 ? s.root__statValue_err : ''}`}>
            {stats.overdue}
          </span>
          <span className={s.root__statLabel}>Просрочено</span>
        </div>
        <div className={s.root__stat}>
          <span className={`${s.root__statValue} ${stats.expiring > 0 ? s.root__statValue_warn : ''}`}>
            {stats.expiring}
          </span>
          <span className={s.root__statLabel}>Истекает (30 дн.)</span>
        </div>
        <div className={s.root__stat}>
          <span className={s.root__statValue}>{stats.active}</span>
          <span className={s.root__statLabel}>В работе</span>
        </div>
        <div className={s.root__stat}>
          <span className={`${s.root__statValue} ${s.root__statValue_ok}`}>{stats.completed}</span>
          <span className={s.root__statLabel}>
            Полнота: {stats.completed} из {stats.total}
          </span>
        </div>
      </div>

      <div className={s.root__section}>
        <div className={s.root__sectionHeader}>
          <div className={s.root__sectionTitle} style={{ marginBottom: 0 }}>
            Требуется по должности
          </div>
          <Button size="small" icon={<IconPlus size={14} />} disabled>
            Назначить
          </Button>
        </div>
        {profile.upcoming_actions.length > 0 ? (
          <>
            {profile.upcoming_actions.map((a, i) => {
              const programId = a.program_id;
              return (
                <div
                  key={`${a.type}-${i}-${programId ?? ''}`}
                  className={s.root__actionRow}
                  onClick={programId ? () => setMatrixDrawerProgramId(programId) : undefined}
                  style={programId ? { cursor: 'pointer' } : undefined}
                  role={programId ? 'button' : undefined}
                >
                  <span>{a.program_name}</span>
                  {a.due_at ? (
                    <Tag color="warning" style={{ marginLeft: 'auto' }}>
                      до {formatDueRu(a.due_at)}
                    </Tag>
                  ) : (
                    <Tag style={{ marginLeft: 'auto' }}>не назначено</Tag>
                  )}
                </div>
              );
            })}
          </>
        ) : (
          <Alert
            type="warning"
            showIcon
            icon={<IconAlertTriangle size={14} />}
            message="Нет требований по должности"
            description="Для должности не найдено программ в матрице обучения (назначения с причиной «матрица»), либо не указана дата приёма для расчёта инструктажей."
            style={{ marginTop: 8 }}
          />
        )}
      </div>

      <ProgramDetailDrawer programId={matrixDrawerProgramId} onClose={() => setMatrixDrawerProgramId(null)} />

      {activeAssignments.length > 0 ? (
        <div className={s.root__section}>
          <div className={s.root__sectionTitle}>Активные обучения</div>
          <Table
            loading={assignmentsLoading}
            dataSource={activeAssignments}
            columns={assignmentColumns}
            rowKey="id"
            size="small"
            pagination={false}
            onRow={(record) => ({
              onClick: () => navigate(`/training/records/${record.id}`),
              style: { cursor: 'pointer' },
            })}
          />
        </div>
      ) : null}
    </div>
  );
}

export function EmployeeTrainingCard() {
  const { employeeId } = useParams<{ employeeId: string }>();
  if (!employeeId) {
    return null;
  }
  return <TrainingEmployeeDetail employeeId={employeeId} showBack />;
}
